import { useEffect, useRef, useState } from 'react';
import { Star, WarningCircle, X } from '@phosphor-icons/react';
import {
  formatProviderTimestamp,
  formatReturn,
  formatUsd,
  returnTone,
} from '../lib/screener.js';
import type { Card } from '../types.js';
import { HistoryChart } from './HistoryChart.js';

type HistoryPoint = { observedAt: string; price: number };

export function DetailDrawer({
  card,
  onClose,
  watched,
  onToggle,
}: {
  card: Card;
  onClose: () => void;
  watched: boolean;
  onToggle: () => void;
}) {
  const [history, setHistory] = useState<HistoryPoint[]>([]);
  const [historyState, setHistoryState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState('');
  const [retryKey, setRetryKey] = useState(0);
  const dialog = useRef<HTMLDialogElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const element = dialog.current;
    if (!element) return;
    const previous = document.activeElement as HTMLElement | null;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    element.showModal();
    closeButton.current?.focus();
    return () => {
      document.body.style.overflow = originalOverflow;
      if (element.open) element.close();
      previous?.focus();
    };
  }, [card.id]);

  useEffect(() => {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 10_000);
    let active = true;
    setHistory([]);
    setHistoryState('loading');
    setError('');
    void fetch(`/api/cards/${encodeURIComponent(card.id)}/history`, { signal: controller.signal })
      .then(response => {
        if (!response.ok) throw new Error('History could not be loaded from the local data service.');
        return response.json() as Promise<{ items: HistoryPoint[] }>;
      })
      .then(value => {
        if (!active) return;
        setHistory(value.items);
        setHistoryState('ready');
      })
      .catch((caught: unknown) => {
        if (!active) return;
        const failure = caught as Error;
        setError(failure.name === 'AbortError' ? 'History request timed out.' : failure.message);
        setHistoryState('error');
      })
      .finally(() => window.clearTimeout(timer));
    return () => {
      active = false;
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [card.id, retryKey]);

  return (
    <dialog
      ref={dialog}
      className="drawer"
      aria-labelledby="detail-title"
      onCancel={event => {
        event.preventDefault();
        onClose();
      }}
      onClick={event => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="drawer-panel">
        <header>
          <div>
            <span className="setline">
              {card.setName} · {card.language === 'ja' ? 'Japanese (JA)' : card.language.toUpperCase()}
            </span>
            <h2 id="detail-title">{card.name}</h2>
            <p>#{card.collectorNumber} · {card.finish}</p>
          </div>
          <button ref={closeButton} type="button" className="icon-button" onClick={onClose} aria-label="Close details">
            <X size={20} />
          </button>
        </header>

        <div className="detail-lead">
          {card.imageUrl
            ? <img src={card.imageUrl} alt={`${card.name}, ${card.setName}, ${card.finish}`} />
            : <div className="image-missing">No provider image</div>}
          <div className="quote">
            <span>Current provider market</span>
            <strong>{card.price === null ? 'Unavailable' : formatUsd(card.price)}</strong>
            <small>Provider snapshot time {formatProviderTimestamp(card.observedAt)}</small>
            <button type="button" className="watch-action" onClick={onToggle}>
              <Star weight={watched ? 'fill' : 'regular'} />
              {watched ? 'Remove from watchlist' : 'Add to watchlist'}
            </button>
          </div>
        </div>

        <div className="return-grid" aria-label="Returns and baselines">
          {Object.entries({
            '24h daily': card.returns.day,
            '7d': card.returns.week,
            '30d': card.returns.month,
            '1y': card.returns.year,
            '5y': card.returns.fiveYear,
          }).map(([label, result]) => (
            <div key={label}>
              <span>{label}</span>
              <strong className={returnTone(result.value)}>
                {formatReturn(result.value)}
              </strong>
              <small>{result.reason ?? `Baseline ${result.baselineDate?.slice(0, 10)}`}</small>
            </div>
          ))}
        </div>

        {historyState === 'loading' && <div className="detail-state" role="status">Loading observed history…</div>}
        {historyState === 'error' && (
          <div className="detail-state error" role="alert">
            <WarningCircle />
            <span>{error}</span>
            <button type="button" onClick={() => setRetryKey(key => key + 1)}>Retry history</button>
          </div>
        )}
        {historyState === 'ready' && <HistoryChart points={history} />}

        <dl className="provenance">
          <div><dt>Source</dt><dd>{card.source.toUpperCase()}</dd></div>
          <div><dt>Basis</dt><dd>{card.priceBasis}</dd></div>
          <div><dt>Market / currency</dt><dd>{card.market} / {card.currency}</dd></div>
          <div><dt>Provider IDs</dt><dd>{card.categoryId} / {card.groupId} / {card.productId}</dd></div>
        </dl>
        {card.sourceUrl && (
          <a className="source-link" href={card.sourceUrl} target="_blank" rel="noreferrer">Open provider listing</a>
        )}
      </div>
    </dialog>
  );
}
